// La demande d'un ticket (docs/TOWER-MULTI.md) : les tickets du même retour,
// un par projet concerné, dans l'ordre, avec ce que chacun attend ; et les
// gestes de l'équipe : ajouter un ticket (avant ou après), en retirer un,
// fixer les tickets amont du ticket affiché.
import type { JSX } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import type { TowerDemandMember, TowerOrigin, TowerProject, TowerState, TowerTicketDetail } from '@feedbaacks/sdk'
import { Button, IconButton, Icons, cx, errorMessage, useFeedbaacks } from '@feedbaacks/ui'
import { href, onLinkClick } from '../router'
import { STATE_META } from './tower-model'
import { Chip, Section } from './TowerParts'

/** Projets par demande (MaxDemandTickets côté serveur). */
export const MAX_DEMAND_TICKETS = 3

const ORIGIN_LABEL: Record<TowerOrigin, string> = {
  triage: '',
  team: 'ajouté par l\'équipe',
  agent: 'besoin déclaré par l\'agent',
}

/** Ajouter un amont, ou changer les dépendances : seulement si aucun agent ne travaille sur le ticket. */
export function canAddBefore(state: TowerState, removed = false): boolean {
  return !removed && (state === 'queued' || state === 'failed' || state === 'stopped')
}

/** Les tickets encore dans la demande. */
export function liveMembers(demand: TowerDemandMember[]): TowerDemandMember[] {
  return demand.filter((m) => !m.removed)
}

/** Les projets qu'on peut encore ajouter : actifs, et pas déjà dans la demande (les tickets retirés libèrent leur projet). */
export function addableProjects(projects: TowerProject[], ticket: Pick<TowerTicketDetail, 'project' | 'demand'>): TowerProject[] {
  const taken = new Set([ticket.project, ...liveMembers(ticket.demand).map((m) => m.project)])
  return projects.filter((p) => p.enabled && !taken.has(p.key))
}

/** Les tickets que le ticket affiché peut attendre : les autres tickets encore dans la demande. */
export function dependencyChoices(ticket: Pick<TowerTicketDetail, 'id' | 'demand'>): TowerDemandMember[] {
  return liveMembers(ticket.demand).filter((m) => m.id !== ticket.id)
}

/** Les projets qu'on peut ajouter à une proposition du tri : actifs, ni le principal ni un déjà proposé, 2 au plus en plus du principal. */
export function proposalAddable(projects: TowerProject[], main: string, also: { project: string }[]): TowerProject[] {
  if (also.length >= MAX_DEMAND_TICKETS - 1) return []
  const taken = new Set([main, ...also.map((a) => a.project)])
  return projects.filter((p) => p.enabled && !taken.has(p.key))
}

function ticketHref(code: string): string {
  return `${href({ name: 'tower' })}?ticket=${encodeURIComponent(code)}`
}

export function TowerDemand({ ticket, onChanged }: { ticket: TowerTicketDetail; onChanged: () => void }): JSX.Element | null {
  const { client, notify, confirm } = useFeedbaacks()
  const [open, setOpen] = useState(false)
  const [projects, setProjects] = useState<TowerProject[] | null>(null)
  const [project, setProject] = useState('')
  const [position, setPosition] = useState<'before' | 'after'>('after')
  const [summary, setSummary] = useState('')
  const [criteria, setCriteria] = useState('')
  const [editingDeps, setEditingDeps] = useState(false)
  const [deps, setDeps] = useState<string[]>(ticket.depends_on)
  const [busy, setBusy] = useState(false)

  const live = liveMembers(ticket.demand)
  const count = Math.max(live.length, 1)
  const full = count >= MAX_DEMAND_TICKETS
  const editable = canAddBefore(ticket.state, ticket.removed)
  const choicesDeps = dependencyChoices(ticket)
  useEffect(() => setDeps(ticket.depends_on), [ticket.id, ticket.depends_on.join(',')])
  useEffect(() => {
    if (!open || projects) return
    client.tower.projects().then(setProjects, () => setProjects([]))
  }, [open])
  const choices = projects ? addableProjects(projects, ticket) : []
  useEffect(() => {
    if (choices.length && !choices.some((p) => p.key === project)) setProject(choices[0].key)
  }, [choices.map((p) => p.key).join(',')])
  useEffect(() => {
    if (!editable && position === 'before') setPosition('after')
  }, [editable])

  const run = async (f: () => Promise<unknown>, ok: string, fail: string): Promise<boolean> => {
    setBusy(true)
    try {
      await f()
      notify('success', ok)
      return true
    } catch (err) {
      notify('error', `${fail} : ${errorMessage(err, 'erreur inattendue')}`)
      return false
    } finally {
      setBusy(false)
      onChanged()
    }
  }
  const submit = async (e: Event) => {
    e.preventDefault()
    const crit = criteria.split('\n').map((c) => c.trim()).filter(Boolean)
    if (!project || !summary.trim() || crit.length === 0) return
    const ok = await run(() => client.tower.addToDemand(ticket.id, { project, position, summary: summary.trim(), criteria: crit }),
      position === 'before'
        ? `Ticket ajouté : ${ticket.code} l'attendra (livré et servi en staging).`
        : `Ticket ajouté : il partira après ${ticket.code}.`, 'Ajout refusé')
    if (ok) {
      setOpen(false)
      setSummary('')
      setCriteria('')
    }
  }
  const remove = async (m: TowerDemandMember) => {
    const yes = await confirm({
      title: `Retirer ${m.code} de la demande ?`,
      message: `${m.code} (${m.project}) s'arrête et n'est plus attendu par les autres tickets. Son travail reste lisible, son projet peut être rajouté plus tard. L'auteur du retour n'en est pas informé.`,
      confirmLabel: 'Retirer', destructive: true,
    })
    if (yes) void run(() => client.tower.removeFromDemand(m.id), `${m.code} retiré de la demande.`, 'Retrait refusé')
  }
  const saveDeps = () => run(() => client.tower.setDependencies(ticket.id, deps), deps.length
    ? `${ticket.code} attend ${deps.join(', ')}.`
    : `${ticket.code} n'attend plus aucun ticket.`, 'Dépendances refusées').then((ok) => ok && setEditingDeps(false))

  if (ticket.demand.length <= 1 && full) return null
  return (
    <Section title="Demande" aside={live.length > 1 ? `${live.length} projets` : undefined}>
      {ticket.demand.length > 1 && (
        <ol class="tw-demand" data-testid="tower-demand">
          {ticket.demand.map((m) => (
            <li key={m.id} class={cx(m.id === ticket.id && 'is-current', m.removed && 'is-removed')} data-testid={`tower-demand-${m.code}`}>
              <a class="fb-mono" href={ticketHref(m.code)} onClick={(e) => onLinkClick(e, ticketHref(m.code))}>{m.code}</a>
              <span class="fb-mono fb-muted">{m.project}</span>
              {m.removed ? <Chip>Retiré</Chip> : <Chip tone={STATE_META[m.state].tone}>{STATE_META[m.state].label}</Chip>}
              {ORIGIN_LABEL[m.origin] && <span class="fb-muted tw-small">{ORIGIN_LABEL[m.origin]}</span>}
              {!m.removed && (m.waiting
                ? <span class="tw-small tw-wait">{m.waiting}</span>
                : m.depends_on.length > 0 && <span class="fb-muted tw-small">après {m.depends_on.join(', ')}</span>)}
              {m.can_remove && (
                <span class="tw-push">
                  <IconButton label={`Retirer ${m.code} de la demande`} disabled={busy} onClick={() => void remove(m)} testId={`tower-demand-remove-${m.code}`}>
                    <Icons.X size={13} />
                  </IconButton>
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
      {ticket.demand.length <= 1 && (
        <p class="fb-muted tw-small">
          Une seule demande peut toucher plusieurs projets, quand elle l'exige vraiment : une évolution de l'API que l'admin doit afficher, par exemple.
        </p>
      )}

      {choicesDeps.length > 0 && !editingDeps && editable && (
        <Button variant="ghost" disabled={busy} onClick={() => setEditingDeps(true)} testId="tower-demand-deps-edit">
          <Icons.Pencil size={13} />Tickets attendus par {ticket.code}
        </Button>
      )}
      {editingDeps && (
        <div class="tw-note-form" data-testid="tower-demand-deps">
          <p class="fb-muted tw-small">{ticket.code} part quand chaque ticket coché est livré et servi en staging. Aucun coché : il part dès qu'une place se libère.</p>
          {choicesDeps.map((m) => (
            <label key={m.id} class="tw-check">
              <input type="checkbox" checked={deps.includes(m.code)} disabled={busy}
                onChange={(e) => setDeps((e.target as HTMLInputElement).checked ? [...deps, m.code] : deps.filter((c) => c !== m.code))}
                data-testid={`tower-demand-dep-${m.code}`} />
              <span class="fb-mono">{m.code}</span> <span class="fb-muted">{m.project}</span>
            </label>
          ))}
          <div class="tw-row">
            <Button onClick={() => { setDeps(ticket.depends_on); setEditingDeps(false) }} disabled={busy}>Annuler</Button>
            <span class="tw-push" />
            <Button variant="primary" disabled={busy} onClick={() => void saveDeps()} testId="tower-demand-deps-save">Enregistrer</Button>
          </div>
        </div>
      )}

      {!full && !open && !ticket.removed && (
        <Button onClick={() => setOpen(true)} testId="tower-demand-add"><Icons.Plus size={14} />Ajouter un ticket à cette demande</Button>
      )}
      {open && (
        <form class="tw-note-form" onSubmit={submit} data-testid="tower-demand-form">
          {projects && choices.length === 0 && <p class="fb-muted tw-small">Aucun autre projet actif à ajouter.</p>}
          {choices.length > 0 && (
            <>
              <div class="tw-row">
                <label class="fb-muted tw-small" for="tw-demand-project">Projet</label>
                <select id="tw-demand-project" class="fb-input" value={project} disabled={busy}
                  onChange={(e) => setProject((e.target as HTMLSelectElement).value)} data-testid="tower-demand-project">
                  {choices.map((p) => <option key={p.key} value={p.key}>{p.label} ({p.key})</option>)}
                </select>
              </div>
              <fieldset class="tw-row tw-small" disabled={busy}>
                <label class="tw-check" title={editable ? '' : 'Seulement quand aucun agent ne travaille sur ce ticket'}>
                  <input type="radio" name="position" checked={position === 'before'} disabled={!editable}
                    onChange={() => setPosition('before')} data-testid="tower-demand-before" />
                  Avant {ticket.code} : il l'attendra
                </label>
                <label class="tw-check">
                  <input type="radio" name="position" checked={position === 'after'} onChange={() => setPosition('after')} />
                  Après {ticket.code}
                </label>
              </fieldset>
              <textarea class="fb-input" rows={2} value={summary} placeholder="Ce qu'il faut changer dans ce projet"
                onInput={(e) => setSummary((e.target as HTMLTextAreaElement).value)} disabled={busy} data-testid="tower-demand-summary" />
              <textarea class="fb-input" rows={3} value={criteria} placeholder="Critères d'acceptation, un par ligne (1 à 5)"
                onInput={(e) => setCriteria((e.target as HTMLTextAreaElement).value)} disabled={busy} data-testid="tower-demand-criteria" />
            </>
          )}
          <div class="tw-row">
            <Button onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
            <span class="tw-push" />
            <Button type="submit" variant="primary" disabled={busy || !project || !summary.trim() || !criteria.trim()} testId="tower-demand-submit">
              <Icons.Plus size={14} />Ajouter
            </Button>
          </div>
        </form>
      )}
    </Section>
  )
}
